import { useRef, useState } from "react";
import {
  Box, Typography, Card, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, Avatar, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Checkbox
} from "@mui/material";
import PersonRounded from "@mui/icons-material/PersonRounded";
import HelpOutline from "@mui/icons-material/HelpOutline";
import LockOutlined from "@mui/icons-material/LockOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import SearchRounded from "@mui/icons-material/SearchRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import ThumbsUpDownOutlined from "@mui/icons-material/ThumbsUpDownOutlined";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { VersionBadge } from "@/components/VersionBadge";
import { useAuthStore } from "@/store/auth-store";
import { useThemeStore } from "@/store/theme-store";
import { useUiVersionStore } from "@/store/ui-version-store";
import { useAccountVisibilityStore } from "@/store/account-visibility-store";
import { useRealAccountsUnfiltered } from "@/lib/whb-data";
import { useSignOutAny } from "@/lib/auth";
import { HeaderSearchMobile } from "@/components/HeaderSearch";
import { AiAssistantPanel } from "@/components/AiAssistantPanel";
import { TawkChatDrawer } from "@/components/TawkChatDrawer";

/**
 * Profile page.
 *
 * V1 & V2 both render the top avatar, the settings card, and the logout
 * card. V2 additionally renders a "Header Actions" card that replicates
 * the TopAppBar's mobile header actions as tappable rows — Search, AI
 * Assistant, Live chat, Feedback, Notifications. In V2 those actions
 * don't live on the header of every page anymore (MobileShellV2 hides
 * the bar except on /profile), so Profile becomes the canonical
 * surface for invoking them. V1 keeps the Profile page unchanged — no
 * need for the duplicate rows when they're already in the header.
 *
 * A UI version toggle row sits at the bottom of the settings card so a
 * V2 user can opt back out without having to find the overflow menu in
 * the (one remaining) TopAppBar. Always visible — the ROW copy changes
 * depending on the current version, same as the overflow menu item.
 */
export function ProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const signOutAny = useSignOutAny();
  const { mode, toggle } = useThemeStore();
  const uiVersion = useUiVersionStore((s) => s.uiVersion);
  const toggleUiVersion = useUiVersionStore((s) => s.toggle);

  // V2-only dialog state. AI panel + Tawk drawer are controlled here
  // (not via the V1 TopAppBar button instances) because in V2 the bar
  // actions live as rows on this page. Search uses the existing
  // HeaderSearchMobile component, which owns its own trigger + dialog —
  // we just render it inside a row-styled wrapper.
  const [aiOpen, setAiOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Visibility dialog — only meaningful under real sessions, where the
  // backend returns every account the user's role can see (admin users
  // see other users' accounts too). Tracked on the auth store's `kind`
  // flag so we can skip rendering the row entirely for demo sessions.
  const [visOpen, setVisOpen] = useState(false);
  const authKind = useAuthStore((s) => s.kind);
  const hiddenCount = useAccountVisibilityStore((s) => s.hiddenIds.length);

  return (
    <Box sx={{ maxWidth: 520, mx: "auto" }}>
      <Stack alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
        <Avatar sx={{ width: 88, height: 88, fontSize: "2rem" }}>
          {(user?.name ?? "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
        </Avatar>
        <Typography variant="h4">{user?.name ?? "Guest"}</Typography>
        <Typography variant="body2" color="text.secondary">{user?.email ?? "—"}</Typography>
      </Stack>

      {/* Header Actions — V2 only. V1 users still have Search / AI /
       *  Live chat / Feedback / Notifications on every page's TopAppBar,
       *  so a duplicate here is noise for them. V2 hides the bar on
       *  every page except Profile, so these rows become the primary
       *  surface for the actions. */}
      {uiVersion === "v2" && (
        <Card sx={{ p: 0, mb: 3 }}>
          <List disablePadding>
            {/* Search — reuses HeaderSearchMobile's fullscreen search
             *  dialog. We wrap the component in a full-width
             *  ListItemButton that invisibly forwards clicks to the
             *  search icon inside. Simpler than re-implementing the
             *  autocomplete list. */}
            <SearchRow />
            <Divider component="li" />
            <Row
              icon={<AutoAwesomeRounded />}
              label="AI Assistant"
              sublabel="Ask Claude about your portfolio and bots"
              trailingChip="BETA"
              onClick={() => setAiOpen(true)}
            />
            <Divider component="li" />
            <Row
              icon={<SupportAgentRounded />}
              label="Live chat"
              sublabel="In-app chat with support"
              onClick={() => setChatOpen(true)}
            />
            <Divider component="li" />
            <Row
              icon={<ThumbsUpDownOutlined />}
              label="Feedback & feature requests"
              sublabel="Post ideas on Nolt"
              onClick={() =>
                window.open(
                  "https://white-hat-bots.nolt.io",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />
            <Divider component="li" />
            <Row
              icon={<NotificationsNoneRounded />}
              label="Notifications"
              sublabel="Portfolio, bot, and account alerts"
              onClick={() => {}}
            />
          </List>
        </Card>
      )}

      <Card sx={{ p: 0, mb: 3 }}>
        <List disablePadding>
          <Row icon={<PersonRounded />}   label="Account"   onClick={() => {}} />
          <Divider component="li" />
          <Row icon={<HelpOutline />}     label="FAQ"       onClick={() => navigate("/help")} />
          <Divider component="li" />
          <Row icon={<LockOutlined />}    label="Privacy"   onClick={() => {}} />
          <Divider component="li" />
          <Row icon={<InfoOutlined />}    label="About us"  onClick={() => {}} />
          <Divider component="li" />
          {/* Account-visibility row — shown only under real sessions
           *  since demo accounts don't have an over-broad backend
           *  response to curate. Trailing chip surfaces the current
           *  hidden count so the user can see the curation state at a
           *  glance without opening the dialog. */}
          {authKind === "real" && (
            <>
              <Row
                icon={<VisibilityOutlined />}
                label="Manage account visibility"
                sublabel="Hide accounts you don't want in the selector"
                trailingChip={hiddenCount > 0 ? `${hiddenCount} hidden` : undefined}
                onClick={() => setVisOpen(true)}
              />
              <Divider component="li" />
            </>
          )}
          <Row
            icon={mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
            label={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}
            onClick={toggle}
          />
          <Divider component="li" />
          {/* UI version toggle — always visible so users can flip
           *  between V1/V2 from either version of the Profile page.
           *  Mirrors the overflow menu item in TopAppBar. */}
          <Row
            icon={<ScienceRounded />}
            label={uiVersion === "v1" ? "Try new design (V2)" : "Back to classic (V1)"}
            sublabel={uiVersion === "v1"
              ? "Cleaner mobile layout — headerless pages, bigger chart"
              : "Restore the current mobile UI with the top header on every page"}
            trailingChip={uiVersion === "v1" ? "NEW" : "V2"}
            onClick={toggleUiVersion}
          />
        </List>
      </Card>

      <Card sx={{ p: 0 }}>
        <List disablePadding>
          <Row icon={<LogoutRounded />} label="Log out" danger
               onClick={() => { signOutAny(); }} />
        </List>
      </Card>

      {/* Build marker — the version was hard-coded here (and went stale
       *  at v0.2.0). Now driven by `APP_VERSION`, and tapping it opens
       *  the release history. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 3 }}
      >
        <Typography variant="caption" color="text.secondary">
          WHB Client
        </Typography>
        <VersionBadge />
      </Stack>

      {/* V2 controlled dialogs — only instantiated when V2 is active so
       *  they don't mount (and fire effects) for V1 users. */}
      {uiVersion === "v2" && (
        <>
          <AiAssistantPanel open={aiOpen} onClose={() => setAiOpen(false)} />
          <TawkChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
        </>
      )}

      {/* Visibility dialog — mounted unconditionally so its internal
       *  `useRealAccountsUnfiltered` hook idles under demo sessions
       *  without side effects. The row-level guard above controls
       *  whether the trigger itself is reachable. */}
      <AccountVisibilityDialog
        open={visOpen}
        onClose={() => setVisOpen(false)}
      />
    </Box>
  );
}

/**
 * Dialog for toggling the hidden state of each real broker account.
 *
 * Existence rationale lives in `store/account-visibility-store.ts`:
 * the backend returns over-broad account lists for admin users
 * (#270), and until a server-side fix lands the PWA offers a
 * client-side allowlist. This dialog is the allowlist's edit
 * surface.
 *
 * Row behaviour:
 *   • Checkbox on = account is **visible** in the selector.
 *   • Checkbox off = account is **hidden** (id lives in the store's
 *     `hiddenIds`).
 *   • "Show all" footer button wipes the hide list so every account
 *     returns to the selector.
 *
 * The list is fetched via `useRealAccountsUnfiltered` — the sister of
 * `useRealAccountsAdapted` that skips the hide filter — so the user
 * can see accounts they've previously hidden and un-hide them.
 */
function AccountVisibilityDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { accounts, loading } = useRealAccountsUnfiltered();
  const hiddenIds = useAccountVisibilityStore((s) => s.hiddenIds);
  const toggle = useAccountVisibilityStore((s) => s.toggle);
  const reset = useAccountVisibilityStore((s) => s.reset);
  const hiddenSet = new Set(hiddenIds);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 700 }}>
        Manage account visibility
      </DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 3, pb: 1.5, display: "block" }}
        >
          WHB's API returns every account your role can access. Untick any
          rows you don't want in the selector. Choices persist across
          refreshes and re-logins; clearing them via "Show all" below
          restores every account.
        </Typography>
        <List dense disablePadding>
          {loading && !accounts && (
            <ListItem>
              <ListItemText
                primary="Loading accounts…"
                primaryTypographyProps={{ color: "text.secondary" }}
              />
            </ListItem>
          )}
          {accounts?.map((a) => {
            const hidden = hiddenSet.has(a.id);
            return (
              <ListItem key={a.id} disablePadding>
                <ListItemButton onClick={() => toggle(a.id)} sx={{ py: 1.25 }}>
                  <Checkbox
                    edge="start"
                    // Checked = visible; unchecked = hidden. The
                    // visual state tracks `!hidden` so the default
                    // (all visible) reads as all-ticks.
                    checked={!hidden}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={a.name}
                    secondary={`${a.provider} · ${a.paper ? "Paper" : "Live"}`}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: "0.75rem" }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          {accounts && accounts.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No real accounts found"
                primaryTypographyProps={{ color: "text.secondary" }}
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={reset} disabled={hiddenIds.length === 0}>
          Show all
        </Button>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({
  icon, label, sublabel, danger, onClick, trailingChip
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  danger?: boolean;
  onClick: () => void;
  trailingChip?: string;
}) {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick} sx={{ px: 2.5, py: 2, borderRadius: 0 }}>
        <ListItemIcon sx={{ minWidth: 40, color: danger ? "error.main" : "primary.main" }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontWeight: 600,
            color: danger ? "error.main" : "text.primary"
          }}
          secondary={sublabel}
          secondaryTypographyProps={{ fontSize: "0.75rem" }}
        />
        {trailingChip && (
          <Chip
            label={trailingChip}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mr: 1, height: 20, fontSize: "0.625rem", fontWeight: 700 }}
          />
        )}
        {!danger && <KeyboardArrowRight sx={{ color: "text.secondary" }} />}
      </ListItemButton>
    </ListItem>
  );
}

/**
 * Row-styled wrapper around HeaderSearchMobile's trigger button. We
 * want the Search row to look like every other row (icon on the left,
 * label, chevron on the right) but open the same fullscreen dialog
 * that the TopAppBar's search button opens. Rather than re-implement
 * the autocomplete, we render HeaderSearchMobile and lay a transparent
 * ListItemButton on top that, when tapped, programmatically clicks the
 * hidden IconButton inside the component. Cheap and correct.
 */
function SearchRow() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  return (
    <ListItem disablePadding sx={{ position: "relative" }}>
      <ListItemButton
        onClick={() => {
          // Find the search icon button inside the hidden
          // HeaderSearchMobile instance and click it. The component
          // renders its own Tooltip-wrapped IconButton with
          // aria-label="Search".
          const btn = containerRef.current?.querySelector<HTMLButtonElement>(
            'button[aria-label="Search"]'
          );
          btn?.click();
        }}
        sx={{ px: 2.5, py: 2, borderRadius: 0 }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
          <SearchRounded />
        </ListItemIcon>
        <ListItemText
          primary="Search"
          primaryTypographyProps={{ fontWeight: 600 }}
          secondary="Accounts, bots, lists"
          secondaryTypographyProps={{ fontSize: "0.75rem" }}
        />
        <KeyboardArrowRight sx={{ color: "text.secondary" }} />
      </ListItemButton>
      {/* Hidden trigger — stashed off-screen but still in the DOM so
       *  we can programmatically click the icon button inside. It
       *  carries the full-screen dialog as a child, which still renders
       *  into the portal as normal when opened. */}
      <Box
        ref={containerRef}
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
          inset: 0
        }}
        aria-hidden
      >
        <HeaderSearchMobile />
      </Box>
    </ListItem>
  );
}
